import { errorResponseSchema } from '@oefy/contracts';
import type { ErrorCode } from '@oefy/contracts';
import type { z } from 'zod';

export type ApiErrorCode = ErrorCode | 'network_error' | 'unexpected_response';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

let lastInput = Date.now();

if (typeof window !== 'undefined') {
  const noteInput = () => {
    lastInput = Date.now();
  };
  for (const event of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
    window.addEventListener(event, noteInput, { passive: true, capture: true });
  }
}

export function idleMs(): number {
  return Math.max(0, Date.now() - lastInput);
}

function idleSeconds(): string {
  return String(Math.floor(idleMs() / 1000));
}

async function send(method: Method, path: string, body?: unknown): Promise<unknown> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Oefy-Idle': idleSeconds(),
  };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? null : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'The server could not be reached');
  }

  if (response.status === 204) {
    return undefined;
  }
  const payload: unknown = await response.json().catch(() => undefined);
  if (response.ok) {
    return payload;
  }
  const failure = errorResponseSchema.safeParse(payload);
  if (failure.success) {
    const { code, message, details } = failure.data.error;
    throw new ApiError(response.status, code, message, details);
  }
  throw new ApiError(response.status, 'internal_error', `Request failed with ${response.status}`);
}

function parse<S extends z.ZodType>(schema: S, payload: unknown): z.output<S> {
  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiError(200, 'unexpected_response', 'The server sent an unexpected response');
  }
  return result.data;
}

export const api = {
  async get<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>> {
    return parse(schema, await send('GET', path));
  },
  async post<S extends z.ZodType>(path: string, body: unknown, schema: S): Promise<z.output<S>> {
    return parse(schema, await send('POST', path, body));
  },
  async put<S extends z.ZodType>(path: string, body: unknown, schema: S): Promise<z.output<S>> {
    return parse(schema, await send('PUT', path, body));
  },
  async patch<S extends z.ZodType>(path: string, body: unknown, schema: S): Promise<z.output<S>> {
    return parse(schema, await send('PATCH', path, body));
  },
  async remove<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>> {
    return parse(schema, await send('DELETE', path));
  },
  async upload<S extends z.ZodType>(
    path: string,
    file: Blob | readonly File[],
    schema: S,
    method: 'POST' | 'PUT' = 'POST',
  ): Promise<z.output<S>> {
    const form = new FormData();
    for (const entry of file instanceof Blob ? [file] : file) {
      form.append('file', entry, entry instanceof File ? entry.name : 'foto.jpg');
    }
    return parse(schema, await send(method, path, form));
  },
  async command(method: Exclude<Method, 'GET'>, path: string, body?: unknown): Promise<void> {
    await send(method, path, body);
  },
};

export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export function errorDetails(error: unknown): string[] {
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) {
    return [];
  }
  return error.details.flatMap((detail: unknown) =>
    typeof detail === 'object' &&
    detail !== null &&
    'message' in detail &&
    typeof detail.message === 'string'
      ? [detail.message]
      : [],
  );
}
